"use client";

import { useEffect, useState, useMemo } from "react";
import {
  User,
  Award,
  TrendingUp,
  Users,
  BookOpen,
  Globe,
  Briefcase,
  Plus,
  Check,
  Target,
  Brain,
  Search,
  Edit,
  X,
  Trash2,
  Info,
  Calendar,
  PlayCircle,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import Modal from "../layout/Modal";
import "./CandidateProfilePage.css";
import BackButton from "../shared/backButton";
import ProfileCard from "../shared/profileCard";
import CustomTooltip from "../shared/CustomTooltip";
import {
  getPeopleById,
  assignGolasInsidePeople,
  deleteGoalInsidePeople,
  getLearningDevelopmentDataApi,
  assignCourseApi,
  deleteCourseApi,
} from "../../services/peopleService";

import { addToTeamApi } from "../../services/teamsService";

import { getAllTeamsApi } from "../../services/teamsService";
import { handleApiError as baseHandleApiError } from "../shared/util";
import SubscriptionExpiredModal from "../dashboard/SubscriptionExpiredModal";
import { useTranslation } from "react-i18next";

import MessageManager from "../layout/Message";

// Inlined AllSkillsModal component
const AllSkillsModal = ({ show, title, skills, onClose }) => {
  const { t } = useTranslation();

  if (!show) {
    return null;
  }

  return (
    <div className="all-skills-modal-overlay-unique">
      <div className="all-skills-modal-content-unique">
        <div className="all-skills-modal-header-unique">
          <h2 className="all-skills-modal-title-unique">{title}</h2>
          <button
            className="all-skills-modal-close-btn-unique"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>
        <div className="all-skills-modal-body-unique">
          {skills.length > 0 ? (
            <div className="all-skills-modal-grid-unique">
              {skills.map((skill, index) => (
                <span
                  key={index}
                  className="all-skills-modal-skill-pill-unique"
                >
                  {skill}
                </span>
              ))}
            </div>
          ) : (
            <p className="all-skills-modal-no-skills-unique">
              {t("common.noSkillsAvailable")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const isDeletedRecord = (item) =>
  item?.isDeleted === true || item?.isDeleted === "true";

const isCompletedStatus = (status) =>
  String(status || "")
    .toLowerCase()
    .includes("complete");

const CandidateProfilePage = ({ onAddToTeam }) => {
  const { t } = useTranslation();
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [learningDevelopmentData, setLearningDevelopmentData] = useState(null);

  const [showModal, setShowModal] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
  });
  const [selectedTeam, setSelectedTeam] = useState("");
  const [skillsSearchTerm, setSkillsSearchTerm] = useState("");
  const [showAllSkills, setShowAllSkills] = useState(false);
  const [newGoal, setNewGoal] = useState("");
  const [goals, setGoals] = useState([
    {
      goal: t("candidateProfile.development.sampleGoals.reactCertification"),
      progress: 75,
      deadline: "2024-06-30",
    },
    {
      goal: t("candidateProfile.development.sampleGoals.leadProject"),
      progress: 45,
      deadline: "2024-09-15",
    },
    {
      goal: t("candidateProfile.development.sampleGoals.mentorTeam"),
      progress: 90,
      deadline: "2024-12-31",
    },
    {
      goal: t("candidateProfile.development.sampleGoals.publicSpeaking"),
      progress: 60,
      deadline: "2024-08-20",
    },
  ]);

  const [showSubscriptionExpired, setShowSubscriptionExpired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState("");

  const handleSubscriptionRedirect = () =>
    new Promise((resolve) => {
      setTimeout(() => {
        navigate("/pricing");
        resolve();
      }, 100);
    });

  const handleApiError = (error, options) => {
    if (error?.response?.status === 403) {
      setSubscriptionMessage(
        error?.response?.data?.message || t("subscription.upgradePlanMessage"),
      );
      setShowSubscriptionExpired(true);
      return;
    }
    return baseHandleApiError(error, options);
  };

  // State for the new AllSkillsModal
  const [showAllSkillsModal, setShowAllSkillsModal] = useState(false);
  const [modalSkillsTitle, setModalSkillsTitle] = useState("");
  const [modalSkillsList, setModalSkillsList] = useState([]);

  // Function to open the AllSkillsModal
  const handleOpenAllSkillsModal = (skills, title) => {
    // Removed type annotations
    setModalSkillsList(skills);
    setModalSkillsTitle(title);
    setShowAllSkillsModal(true);
  };

  // Function to close the AllSkillsModal
  const handleCloseAllSkillsModal = () => {
    setShowAllSkillsModal(false);
    setModalSkillsList([]);
    setModalSkillsTitle("");
  };

  const [dngActiveSkillType, setDngActiveSkillType] = useState("job"); // "job" | "soft"
  const [dngDeleted, setDngDeleted] = useState({}); // { [skillName]: Set(courseId) }
  const [dngGoals, setDngGoals] = useState([]); // [{ skill, course: { id, course_name, level, rating, platform, url }, dueDate: '' }]
  const hasLD = !!learningDevelopmentData;

  const ldMemo = useMemo(() => {
    if (!learningDevelopmentData) return null;

    const normalize = (arr) =>
      (arr || [])
        .filter((s) => !isDeletedRecord(s))
        .map((s) => ({
          name: s.skill_gap,
          steps: s.steps || [],
          skillStatus: s.skillStatus || null,
          courses: (s.recommended_courses || [])
            .filter((c) => !isDeletedRecord(c))
            .map((c) => ({ ...c, id: c.course_id || c.id })),
        }));

    return {
      soft: normalize(learningDevelopmentData.softSkills),
      job: normalize(learningDevelopmentData.jobSkills),
    };
  }, [learningDevelopmentData]);

  const dngSkillsForTab = useMemo(() => {
    const bucket = ldMemo
      ? dngActiveSkillType === "job"
        ? ldMemo.job
        : ldMemo.soft
      : [];
    if (!Array.isArray(bucket)) return [];

    return bucket
      .map((skill, index) => {
        const skillName = typeof skill === "string" ? skill : skill?.name || "";
        const deletedCourses = dngDeleted[skillName];
        const visibleCourses = (skill?.courses || []).filter(
          (course) => !(deletedCourses && deletedCourses.has(course.id)),
        );
        const allCoursesCompleted =
          visibleCourses.length > 0 &&
          visibleCourses.every((course) => isCompletedStatus(course.status));

        return { skill, index, visibleCourses, allCoursesCompleted };
      })
      .filter(({ visibleCourses }) => visibleCourses.length > 0)
      .sort((a, b) => {
        if (a.allCoursesCompleted !== b.allCoursesCompleted) {
          return a.allCoursesCompleted ? 1 : -1;
        }
        return a.index - b.index;
      })
      .map(({ skill }) => skill);
  }, [ldMemo, dngActiveSkillType, dngDeleted]);
  // Prevent selecting past dates in <input type="date">
  const todayMin = useMemo(() => {
    // make an ISO date string in local time (avoids UTC off-by-one)
    const tzOffset = new Date().getTimezoneOffset(); // minutes
    const local = new Date(Date.now() - tzOffset * 60 * 1000);
    return local.toISOString().split("T")[0]; // "YYYY-MM-DD"
  }, []);

  // Generate two front-end-only recommended courses per skill using the Learning Path card design cues
  // const dngRecommendedCourses = (skill) => {
  //   // When LD data exists, use the real courses from API (already normalized)
  //   if (ld && skill && Array.isArray(skill.courses)) {
  //     return skill.courses;
  //   }
  //   // Fallback to demo data when API not available
  //   // const base = typeof skill === "string" ? skill : String(skill?.name || "");
  // };

  // const dngRecommendedCourses = (skill) => {
  //   if (ld && skill && Array.isArray(skill.courses)) return skill.courses;
  //   return []; // no static/demo data
  // };

  const dngRecommendedCourses = (skill) =>
    Array.isArray(skill?.courses) ? skill.courses : [];

  const dngIsDeleted = (skill, id) => {
    const set = dngDeleted[skill];
    return set && set.has(id);
  };

  const dngDeleteCourse = async (skill, id) => {
    // setDngDeleted((prev) => {
    //   const next = { ...prev };
    //   const set = new Set(next[skill] || []);
    //   set.add(id);
    //   next[skill] = set;
    //   return next;
    // });

    try {
      // confirmation before deletion
      setShowModal({
        show: true,
        type: "warning",
        title: t("candidateProfile.development.deleteCourseTitle"),
        message: t("candidateProfile.development.deleteCourseMessage"),
        showButton: true,

        onConfirm: async () => {
          try {
            const obj = {
              userId: safeCandidate._id,

              courseId: id,
            };
            console.log("Confirming course deletion:", obj);
            const res = await deleteCourseApi(obj);
            if (res.code === 200) {
              setDngDeleted((prev) => {
                const next = { ...prev };
                const set = new Set(next[skill] || []);
                set.add(id);
                next[skill] = set;
                return next;
              });
              setShowModal({
                show: true,
                type: "success",
                title: t("common.success"),
                message:
                  res.message ||
                  t("candidateProfile.development.courseDeletedSuccess"),
                showButton: false,
              });

              getLearningDevelopmentDataApiHandler(paramId);
            }
          } catch (error) {
            handleApiError(error, { showModal: true });
          }
        },
      });
    } catch (error) {
      handleApiError(error, { showModal: true });
    }
  };

  const dngAssignCourse = (skill, course) => {
    setDngGoals((prev) => {
      const exists = prev.some(
        (g) => g.skill === skill && g.course.id === course.id,
      );
      if (exists) return prev;
      return [...prev, { skill, course, dueDate: "" }];
    });
  };

  const dngUpdateDueDate = (skill, courseId, dueDate) => {
    setDngGoals((prev) =>
      prev.map((g) =>
        g.skill === skill && g.course.id === courseId ? { ...g, dueDate } : g,
      ),
    );
  };

  const dngConfirmAssignment = async (skill, course) => {
    const goal = dngGoals.find(
      (g) => g.skill === skill && g.course.id === course.id,
    );

    if (!goal || !goal.dueDate) {
      setShowModal({
        show: true,
        type: "warning",
        title: t("candidateProfile.development.selectDueDateTitle"),
        message: t("candidateProfile.development.selectDueDateMessage"),
        showButton: false,
      });
      return;
    }

    try {
      const obj = {
        userId: safeCandidate._id,
        courseId: course.id || course.course_id,
        dueDate: goal.dueDate, // YYYY-MM-DD from <input type="date" />
      };

      console.log("Confirming course assignment:", obj);
      const res = await assignCourseApi(obj);

      if (res.code === 200) {
        MessageManager.showToast(
          "success",
          t("candidateProfile.skills.courseAssignSuccessToast"),
        );

        // Optional: refresh API-backed view so assigned status & due date reflect server
        await getLearningDevelopmentDataApiHandler(paramId);

        // Clear the staged row (optional – keeps UI tidy)
        setDngGoals((prev) =>
          prev.filter((g) => !(g.skill === skill && g.course.id === course.id)),
        );
      }
    } catch (error) {
      handleApiError(error, { showModal: true });
    }
  };

  // const dngUpdateDueDate = async (skill, courseId, dueDate) => {
  //   setDngGoals((prev) =>
  //     prev.map((g) =>
  //       g.skill === skill && g.course.id === courseId ? { ...g, dueDate } : g
  //     )
  //   );

  //   try {
  //     const obj = {
  //
  //       courseId: courseId,
  //       dueDate: dueDate,
  //     };
  //     console.log()
  //     const res = await assignCourseApi(obj);
  //     console.log("Course assigned successfully:", res);
  //     if (res.code === 200) {
  //       // Handle success case
  //       setShowModal({
  //         show: true,
  //         type: "success",
  //         title: "Success",
  //         message: res.message || "Course assigned successfully",
  //         showButton: false,
  //       });
  //     }
  //   } catch (error) {
  //     console.error("Error updating due date:", error);
  //   }
  // };

  const dngClearAssignment = (skill, courseId) => {
    setDngGoals((prev) =>
      prev.filter((g) => !(g.skill === skill && g.course.id === courseId)),
    );
  };

  const dngSkillStatus = (skill) => {
    const assignments = dngGoals.filter((g) => g.skill === skill);
    if (assignments.length === 0) return "notstarted";
    const now = new Date();
    const isPast = assignments.some(
      (g) => g.dueDate && new Date(g.dueDate) < now,
    );
    return isPast ? "completed" : "pursuing";
  };

  const dngEmail = (skill) => {
    // Frontend-only: just show a confirmation using the existing Modal
    setShowModal({
      show: true,
      type: "success",
      title: t("common.success"),
      message: t("candidateProfile.development.assignedCourseWillBeSent"),
      showButton: false,
    });
  };
  const dngStatusLabel = (status) => {
    if (status === "completed")
      return t("candidateProfile.development.status.completed");
    if (status === "pursuing")
      return t("candidateProfile.development.status.pursuing");
    if (status === "notstarted")
      return t("candidateProfile.development.status.notStarted");

    return t("candidateProfile.development.status.notAvailable");
  };

  // Map a course.status string to a normalized key we use for styling
  const normalizeCourseStatus = (s = "") => {
    const v = String(s).trim().toLowerCase();
    if (["completed", "complete", "done"].includes(v)) return "completed";
    if (["pursuing", "in progress", "ongoing"].includes(v)) return "pursuing";
    if (["not started", "planned", "pending"].includes(v)) return "notstarted";
    return "unknown";
  };

  // Human-friendly label for the chip
  const statusLabelFromKey = (key) =>
    ({
      completed: t("candidateProfile.development.status.completed"),
      pursuing: t("candidateProfile.development.status.pursuing"),
      notstarted: t("candidateProfile.development.status.notStarted"),
      unknown: t("candidateProfile.development.status.notAvailable"),
    })[key] || t("candidateProfile.development.status.notAvailable");
  // Derive a single status for a skill from its courses
  const skillStatusFromCourses = (skill) => {
    const courses = Array.isArray(skill?.courses) ? skill.courses : [];
    if (courses.length === 0) return "notstarted";
    const keys = courses.map((c) => normalizeCourseStatus(c?.status));

    if (keys.some((k) => k === "pursuing")) return "pursuing";

    if (keys.every((k) => k === "completed")) return "completed";

    if (keys.every((k) => k === "notstarted" || k === "unknown"))
      return "notstarted";

    return "pursuing";
  };

  // Check if user is manager (you can implement your own logic here)
  const isManager = true; // This should come from your auth/user context

  // Soft skills - only showing names now
  const softSkills = [
    {
      name: t("candidateProfile.skills.softSkillsList.communication"),
      score: 8,
    },
    { name: t("candidateProfile.skills.softSkillsList.leadership"), score: 7 },
    { name: t("candidateProfile.skills.softSkillsList.teamwork"), score: 9 },
    {
      name: t("candidateProfile.skills.softSkillsList.problemSolving"),
      score: 8,
    },
    {
      name: t("candidateProfile.skills.softSkillsList.adaptability"),
      score: 6,
    },
    {
      name: t("candidateProfile.skills.softSkillsList.timeManagement"),
      score: 7,
    },
    {
      name: t("candidateProfile.skills.softSkillsList.criticalThinking"),
      score: 8,
    },
    { name: t("candidateProfile.skills.softSkillsList.creativity"), score: 6 },
    {
      name: t("candidateProfile.skills.softSkillsList.emotionalIntelligence"),
      score: 9,
    },
    {
      name: t("candidateProfile.skills.softSkillsList.conflictResolution"),
      score: 5,
    },
    {
      name: t("candidateProfile.skills.softSkillsList.decisionMaking"),
      score: 7,
    },
    { name: t("candidateProfile.skills.softSkillsList.negotiation"), score: 6 },
    {
      name: t("candidateProfile.skills.softSkillsList.publicSpeaking"),
      score: 5,
    },
    {
      name: t("candidateProfile.skills.softSkillsList.activeListening"),
      score: 8,
    },
    { name: t("candidateProfile.skills.softSkillsList.empathy"), score: 9 },
    {
      name: t("candidateProfile.skills.softSkillsList.stressManagement"),
      score: 6,
    },
    { name: t("candidateProfile.skills.softSkillsList.flexibility"), score: 7 },
    { name: t("candidateProfile.skills.softSkillsList.initiative"), score: 8 },
    {
      name: t("candidateProfile.skills.softSkillsList.collaboration"),
      score: 9,
    },
    { name: t("candidateProfile.skills.softSkillsList.patience"), score: 7 },
    { name: t("candidateProfile.skills.softSkillsList.persuasion"), score: 6 },
    { name: t("candidateProfile.skills.softSkillsList.mentoring"), score: 5 },
    {
      name: t("candidateProfile.skills.softSkillsList.culturalAwareness"),
      score: 7,
    },
    { name: t("candidateProfile.skills.softSkillsList.networking"), score: 6 },
    {
      name: t("candidateProfile.skills.softSkillsList.presentationSkills"),
      score: 5,
    },
    {
      name: t("candidateProfile.skills.softSkillsList.interpersonalSkills"),
      score: 8,
    },
    {
      name: t("candidateProfile.skills.softSkillsList.selfMotivation"),
      score: 8,
    },
    {
      name: t("candidateProfile.skills.softSkillsList.accountability"),
      score: 9,
    },
    { name: t("candidateProfile.skills.softSkillsList.resilience"), score: 7 },
    { name: t("candidateProfile.skills.softSkillsList.innovation"), score: 6 },
    {
      name: t("candidateProfile.skills.softSkillsList.strategicThinking"),
      score: 7,
    },
    {
      name: t("candidateProfile.skills.softSkillsList.analyticalThinking"),
      score: 8,
    },
    {
      name: t("candidateProfile.skills.softSkillsList.attentionToDetail"),
      score: 9,
    },
    {
      name: t("candidateProfile.skills.softSkillsList.organization"),
      score: 8,
    },
    {
      name: t("candidateProfile.skills.softSkillsList.multitasking"),
      score: 6,
    },
    {
      name: t("candidateProfile.skills.softSkillsList.prioritization"),
      score: 7,
    },
    { name: t("candidateProfile.skills.softSkillsList.goalSetting"), score: 8 },
    {
      name: t("candidateProfile.skills.softSkillsList.selfAwareness"),
      score: 7,
    },
    { name: t("candidateProfile.skills.softSkillsList.confidence"), score: 6 },
    { name: t("candidateProfile.skills.softSkillsList.integrity"), score: 10 },
    { name: t("candidateProfile.skills.softSkillsList.workEthic"), score: 9 },
    {
      name: t("candidateProfile.skills.softSkillsList.professionalism"),
      score: 9,
    },
    { name: t("candidateProfile.skills.softSkillsList.reliability"), score: 8 },
    { name: t("candidateProfile.skills.softSkillsList.punctuality"), score: 9 },
    { name: t("candidateProfile.skills.softSkillsList.curiosity"), score: 7 },
    {
      name: t("candidateProfile.skills.softSkillsList.openMindedness"),
      score: 8,
    },
    { name: t("candidateProfile.skills.softSkillsList.diplomacy"), score: 6 },
    { name: t("candidateProfile.skills.softSkillsList.coaching"), score: 5 },
    { name: t("candidateProfile.skills.softSkillsList.delegation"), score: 6 },
    { name: t("candidateProfile.skills.softSkillsList.vision"), score: 7 },
    { name: t("candidateProfile.skills.softSkillsList.influence"), score: 6 },
    { name: t("candidateProfile.skills.softSkillsList.motivation"), score: 8 },
    {
      name: t("candidateProfile.skills.softSkillsList.teamBuilding"),
      score: 7,
    },
    {
      name: t("candidateProfile.skills.softSkillsList.performanceManagement"),
      score: 6,
    },
    {
      name: t("candidateProfile.skills.softSkillsList.changeManagement"),
      score: 5,
    },
    {
      name: t("candidateProfile.skills.softSkillsList.riskAssessment"),
      score: 7,
    },
    {
      name: t("candidateProfile.skills.softSkillsList.qualityFocus"),
      score: 8,
    },
    {
      name: t("candidateProfile.skills.softSkillsList.customerService"),
      score: 9,
    },
    { name: t("candidateProfile.skills.softSkillsList.salesSkills"), score: 6 },
    {
      name: t("candidateProfile.skills.softSkillsList.marketAwareness"),
      score: 7,
    },
    {
      name: t("candidateProfile.skills.softSkillsList.businessAcumen"),
      score: 6,
    },
    {
      name: t("candidateProfile.skills.softSkillsList.financialLiteracy"),
      score: 5,
    },
    {
      name: t("candidateProfile.skills.softSkillsList.projectManagement"),
      score: 7,
    },
    {
      name: t("candidateProfile.skills.softSkillsList.resourceManagement"),
      score: 6,
    },
    {
      name: t("candidateProfile.skills.softSkillsList.vendorManagement"),
      score: 5,
    },
    { name: t("candidateProfile.skills.stakeholderManagement"), score: 7 },
    {
      name: t("candidateProfile.skills.crossFunctionalCollaboration"),
      score: 8,
    },
    { name: t("candidateProfile.skills.remoteWorkSkills"), score: 8 },
    { name: t("candidateProfile.skills.digitalCommunication"), score: 9 },
    { name: t("candidateProfile.skills.virtualPresentation"), score: 6 },
    { name: t("candidateProfile.skills.onlineCollaboration"), score: 8 },
    { name: t("candidateProfile.skills.techSavviness"), score: 7 },
    { name: t("candidateProfile.skills.dataInterpretation"), score: 6 },
    { name: t("candidateProfile.skills.researchSkills"), score: 7 },
    { name: t("candidateProfile.skills.documentation"), score: 8 },
    { name: t("candidateProfile.skills.processImprovement"), score: 6 },
    { name: t("candidateProfile.skills.qualityAssurance"), score: 7 },
    { name: t("candidateProfile.skills.compliance"), score: 8 },
    { name: t("candidateProfile.skills.ethics"), score: 10 },
    { name: t("candidateProfile.skills.diversityInclusion"), score: 8 },
    { name: t("candidateProfile.skills.sustainabilityMindset"), score: 7 },
    { name: t("candidateProfile.skills.globalPerspective"), score: 6 },
    { name: t("candidateProfile.skills.continuousLearning"), score: 9 },
    { name: t("candidateProfile.skills.knowledgeSharing"), score: 8 },
    { name: t("candidateProfile.skills.feedbackReception"), score: 7 },
    { name: t("candidateProfile.skills.feedbackDelivery"), score: 6 },
    { name: t("candidateProfile.skills.performanceCoaching"), score: 5 },
    { name: t("candidateProfile.skills.careerDevelopment"), score: 6 },
  ];

  const jobSkills = [
    { name: "JavaScript", score: 95 },
    { name: "React", score: 92 },
    { name: "Python", score: 90 },
    { name: "Node.js", score: 88 },
    { name: "TypeScript", score: 87 },
    { name: "AWS", score: 85 },
    { name: "SQL", score: 84 },
  ];

  // Performance categories with skill gaps
  const performanceCategories = [
    {
      name: t(
        "candidateProfile.skills.performanceCategories.technicalSkills.name",
      ),
      score: 8.5,
      description: t(
        "candidateProfile.skills.performanceCategories.technicalSkills.description",
      ),
      currentSkills: [
        "JavaScript",
        "React",
        "Python",
        "HTML",
        "CSS",
        "Algorithms",
        "Data Structures",
      ],
      gaps: [
        "Advanced AWS",
        "Kubernetes",
        "GraphQL",
        "Microservices",
        "DevOps Practices",
      ],
    },
    {
      name: t(
        "candidateProfile.skills.performanceCategories.communication.name",
      ),
      score: 9.1,
      description: t(
        "candidateProfile.skills.performanceCategories.communication.description",
      ),
      currentSkills: [
        "Presentation",
        "Team Collaboration",
        "Written Reports",
        "Active Listening",
      ],
      gaps: [
        "Public Speaking",
        "Cross-cultural Communication",
        "Negotiation Skills",
      ],
    },
    {
      name: t("candidateProfile.skills.performanceCategories.leadership.name"),
      score: 7.8,
      description: t(
        "candidateProfile.skills.performanceCategories.leadership.description",
      ),
      currentSkills: [
        "Team Coordination",
        "Project Planning",
        "Delegation",
        "Mentoring Junior Staff",
      ],
      gaps: [
        "Strategic Planning",
        "Conflict Resolution",
        "Change Management",
        "Vision Setting",
      ],
    },
    {
      name: t(
        "candidateProfile.skills.performanceCategories.problemSolving.name",
      ),
      score: 8.7,
      description: t(
        "candidateProfile.skills.performanceCategories.problemSolving.description",
      ),
      currentSkills: [
        "Data Analysis",
        "Debugging",
        "Research",
        "Root Cause Analysis",
      ],
      gaps: ["Systems Thinking", "Innovation", "Complex Problem Decomposition"],
    },
  ];

  // Replace the existing skillGapsFromUpskill array with these two arrays:
  const jobSkillsGaps = [
    { name: "Advanced AWS", priority: t("common.priority.high") },
    { name: "Kubernetes", priority: t("common.priority.high") },
    { name: "GraphQL", priority: t("common.priority.medium") },
    { name: "Systems Thinking", priority: t("common.priority.high") },
  ];

  const softSkillsGaps = [
    { name: t("candidateProfile.skills.softSkillsGaps.publicSpeaking") },
    { name: t("candidateProfile.skills.softSkillsGaps.strategicPlanning") },
    { name: t("candidateProfile.skills.softSkillsGaps.conflictResolution") },
    {
      name: t(
        "candidateProfile.skills.softSkillsGaps.crossCulturalCommunication",
      ),
    },
    { name: t("candidateProfile.skills.softSkillsGaps.mentoring") },
    { name: t("candidateProfile.skills.softSkillsGaps.innovation") },
  ];

  // Training data with manager controls
  const [trainingData, setTrainingData] = useState([
    {
      course: t(
        "candidateProfile.development.sampleTraining.advancedJavaScript",
      ),
      platform: "coursera",
      status: t("candidateProfile.development.trainingStatus.completed"),
      date: "2024-02-15",
      score: 95,
    },
    {
      course: t(
        "candidateProfile.development.sampleTraining.leadershipFundamentals",
      ),
      platform: "linkedin",
      status: t("candidateProfile.development.trainingStatus.inProgress"),
      date: "2024-03-01",
      score: null,
    },
    {
      course: t(
        "candidateProfile.development.sampleTraining.dataAnalysisPython",
      ),
      platform: "udemy",
      status: t("candidateProfile.development.trainingStatus.completed"),
      date: "2024-01-20",
      score: 88,
    },
    {
      course: t(
        "candidateProfile.development.sampleTraining.projectManagement",
      ),
      platform: "coursera",
      status: t("candidateProfile.development.trainingStatus.planned"),
      date: "2024-05-01",
      score: null,
    },
  ]);

  // Sample stats for this candidate
  const candidateStats = {
    upskill: {
      needsTraining: true,
      urgency: t("common.priority.medium"),
      missingSkills: 3,
      completedTraining: 2,
    },
    leadership: {
      potential: t("common.priority.high"),
      readiness: 85,
      score: 8.7,
      nominations: 1,
    },
    mobility: {
      opportunities: 4,
      bestMatch: 92,
      recommendations: 2,
      transitions: 1,
    },
  };

  const getScoreColor = (score) => {
    if (score >= 9) return "score-excellent-unique";
    if (score >= 8) return "score-good-unique";
    return "score-average-unique";
  };

  const getMatchColor = (match) => {
    if (match >= 90) return "match-excellent-unique";
    if (match >= 80) return "match-good-unique";
    return "match-average-unique";
  };

  const getPriorityColor = (priority) => {
    switch (priority.toLowerCase()) {
      case "high":
        return "priority-high-unique";
      case "medium":
        return "priority-medium-unique";
      case "low":
        return "priority-low-unique";
      default:
        return "priority-medium-unique";
    }
  };

  const handleAddToTeam = async () => {
    if (!selectedTeam) {
      setShowModal({
        show: true,
        type: "warning",
        title: t("candidateProfile.actions.pleaseSelectTeam"),
        message: t("candidateProfile.actions.selectTeamMessage"),
        showButton: false,
      });
      return;
    }
    try {
      const userDetails = [
        {
          // userId: safeCandidate.userId,
          userId: safeCandidate._id,

          name: safeCandidate.name,
        },
      ];
      const obj = {
        userDetails: userDetails,
        teamId: selectedTeam,
      };
      console.log("Adding to team with data:", obj);
      const response = await addToTeamApi(obj);
      if (response.code === 200) {
        setSelectedTeam("");
        getUserByIdApiHandler();
        MessageManager.showToast(
          "success",
          t("candidateProfile.actions.profileAddedSuccess"),
        );
        // Optionally, you can refresh the teams list or perform other actions
        getAllTeamsApiHandler();
      }
    } catch (error) {
      handleApiError(error, {
        showModal: true,
        showButton: false,
      });
    }
  };

  const handleComplaint = () => {
    setShowModal({
      show: true,
      type: "info",
      title: t("candidateProfile.actions.submitComplaint"),
      message: t("candidateProfile.actions.complaintMessage"),
    });
  };

  const handleEditProfile = () => {
    // onNavigate("/employees/edit/id", safeCandidate.userId);
    navigate(`/employees/edit/${safeCandidate._id}`);
  };

  const handleTrainingStatusChange = (index, newStatus) => {
    const updatedTraining = [...trainingData];
    updatedTraining[index].status = newStatus;
    setTrainingData(updatedTraining);
  };

  const tabs = [
    { id: "overview", label: t("candidateProfile.tabs.overview"), icon: User },
    { id: "skills", label: t("candidateProfile.tabs.skills"), icon: Brain },
    {
      id: "development",
      label: t("candidateProfile.tabs.development"),
      icon: Target,
    },
    { id: "team", label: t("candidateProfile.tabs.team"), icon: Users },
  ];

  const navigate = useNavigate();
  const onNavigate = (page) => {
    console.log("Navigating to:", page);
    // Add candidate ID as URL parameter
    const candidateId = safeCandidate._id || 1;
    navigate(`${page}?candidateId=${candidateId}`);
  };

  const [candidate, setCandidate] = useState(null);
  const paramId = useParams().id; //userId of the employee profile

  // API CALLS
  useEffect(() => {
    getUserByIdApiHandler();
    getAllTeamsApiHandler();
    getLearningDevelopmentDataApiHandler(paramId);
  }, []);
  const getUserByIdApiHandler = async () => {
    console.log("Fetching user by ID:", paramId);
    try {
      const response = await getPeopleById(paramId);
      setCandidate(response.data);
    } catch (error) {
      handleApiError(error, {
        showModal: false,
      });
    }
  };

  const getLearningDevelopmentDataApiHandler = async (userId) => {
    try {
      const response = await getLearningDevelopmentDataApi(userId);
      setLearningDevelopmentData(response.data);
      console.log("Learning & Development Data:", response.data);
    } catch (error) {
      handleApiError(error, {
        showModal: false,
      });
    }
  };
  // Derived flag & normalized view of Learning & Development API

  const ld = useMemo(() => {
    if (!learningDevelopmentData) return null;

    const normalize = (arr) =>
      (arr || [])
        .filter((s) => !isDeletedRecord(s))
        .map((s) => ({
          // name is what the current UI expects
          name: s.skill_gap,
          // keep steps as-is
          steps: s.steps || [],
          // map recommended_courses and give each an `id` for UI reuse
          courses: (s.recommended_courses || [])
            .filter((c) => !isDeletedRecord(c)) // hide deleted ones
            .map((c) => ({ ...c, id: c.course_id || c.id })),
        }));

    return {
      soft: normalize(learningDevelopmentData.softSkills),
      job: normalize(learningDevelopmentData.jobSkills),
    };
  }, [learningDevelopmentData]);

  // --- Add: derive API-assigned goals from LD (is_assigned === true) ---
  const apiAssignedGoals = useMemo(() => {
    if (!ldMemo) return [];
    const fromBucket = (bucket) =>
      (bucket || []).flatMap((s) =>
        (s.courses || [])
          .filter((c) => c.is_assigned)
          .map((c) => ({
            source: "api",
            skill: s.name,
            course: c, // has id, course_name, level, platform, rating, due_date, is_assigned
            dueDate: c.due_date || "",
          })),
      );
    console.log(fromBucket(ldMemo.job), "fromBucket job");
    return [...fromBucket(ldMemo.job), ...fromBucket(ldMemo.soft)];
  }, [ldMemo]);

  // --- Add: merge API-assigned + locally staged (dedupe by skill+course.id; prefer API) ---
  const dngAllGoals = useMemo(() => {
    const map = new Map();

    // prefer API entries
    apiAssignedGoals.forEach((g) => {
      const key = `${g.skill}::${g.course.id}`;
      map.set(key, g);
    });

    // add local staged ones only if not already present from API
    dngGoals.forEach((g) => {
      const key = `${g.skill}::${g.course.id}`;
      if (!map.has(key)) {
        map.set(key, { source: "local", ...g });
      }
    });

    return Array.from(map.values());
  }, [apiAssignedGoals, dngGoals]);

  // Create safe candidate object with defaults for missing properties
  const safeCandidate = {
    name: candidate?.name || t("common.notAvailable"),
    role: candidate?.designation || t("common.notAvailable"),
    department: candidate?.department || t("common.notAvailable"),
    userId: candidate?.userId || t("common.notAvailable"),
    skillsToDevelop: candidate?.skillsToDevelop || 0,
    leadershipReadyScore: candidate?.leadershipReadyScore || 0,
    mobilityOpportunities: candidate?.mobilityOpportunities || 0,
    joinDate: candidate?.joinedDate || "2022-01-01",
    education: candidate?.education,
    experience: candidate?.experience || t("common.notSpecified"),
    performanceRating: candidate?.performanceRating || t("common.notSpecified"),
    leadershipScore:
      candidate?.leadershipScore || Math.floor(Math.random() * 3) + 7,
    skills: candidate?.skills || candidate?.missingSkills || [],
    teamFitScore: candidate?.teamFitScore || 0,
    languages: candidate?.languages || [t("common.languages.english")],
    interests: candidate?.interests || [
      t("common.interests.professionalDevelopment"),
    ],
    certifications: candidate?.certifications || [
      t("common.certifications.professional"),
    ],
    projects: candidate?.projects || [t("common.projects.current")],
    reportsTO: candidate?.directReportsTo || "0",
    manager: candidate?.manager || t("common.notSpecified"),
    salary: candidate?.salary || t("common.notDisclosed"),
    teamFit: candidate?.teamFit || Math.floor(Math.random() * 20) + 75,
    jobSkills: candidate?.jobSkills || jobSkills,
    jobSkillsGaps:
      candidate?.skillGapsForUpskilling?.jobSkills || jobSkillsGaps,
    softSkillsGaps:
      candidate?.skillGapsForUpskilling?.softSkills || softSkillsGaps,
    performanceCategories:
      candidate?.performanceCategories || performanceCategories,
    displayedSkills: candidate?.allSubSoftSkills || softSkills,
    goals: candidate?.currentGoals || goals,
    trainingData: candidate?.currentCourses || trainingData,
    ...candidate,

    teamDetails: candidate?.teamDetails,
    teamMatches: candidate?.teamMatches,
    level: candidate?.level || t("common.notAvailable"),
    managerName: candidate?.managerName || t("common.notAvailable"),
    _id: candidate?._id || "",
  };

  // API-only, no front-end calculations
  const apiGoals = useMemo(() => {
    if (!learningDevelopmentData) return { assigned: [], unassigned: [] };

    const flatten = (skills = []) =>
      skills
        .filter((s) => !isDeletedRecord(s))
        .flatMap((s) =>
          (s.recommended_courses || [])
            .filter((c) => !isDeletedRecord(c))
            .map((c) => ({
              id: c.course_id || c.id,
              skill: s.skill_gap, // column: Skill
              course: c.course_name, // column: Course
              dueDate: c.due_date, // column: Due Date
              status: c.status, // column: Status
              isAssigned: !!c.is_assigned,
            })),
        );

    const items = [
      ...flatten(learningDevelopmentData.jobSkills || []),
      ...flatten(learningDevelopmentData.softSkills || []),
    ];
    const sortByCompletion = (rows) =>
      rows
        .map((row, index) => ({ row, index }))
        .sort((a, b) => {
          const aCompleted = isCompletedStatus(a.row.status);
          const bCompleted = isCompletedStatus(b.row.status);

          if (aCompleted !== bCompleted) {
            return aCompleted ? 1 : -1;
          }

          return a.index - b.index;
        })
        .map(({ row }) => row);

    return {
      assigned: sortByCompletion(items.filter((x) => x.isAssigned)),
      unassigned: items.filter((x) => !x.isAssigned),
    };
  }, [learningDevelopmentData]);
  const statusChipClass = (status) => {
    const v = String(status || "").toLowerCase();
    if (v.includes("complete")) return "dng-past-chip"; // Completed
    if (
      v.includes("pursuing") ||
      v.includes("progress") ||
      v.includes("ongoing")
    )
      return "dng-current-chip"; // Pursuing / In progress
    return "dng-pending-chip"; // Not Started / default
  };

  const isCourseCompleted = (course) => {
    return isCompletedStatus(course?.status);
  };

  // Filter skills based on search term
  const filteredSkills = safeCandidate.displayedSkills.filter((s) => {
    const name = typeof s === "string" ? s : (s?.name ?? "");
    return name.toLowerCase().includes(skillsSearchTerm.toLowerCase());
  });

  // Show top skills first, then load more
  const displayedSkills = showAllSkills
    ? filteredSkills
    : filteredSkills.slice(0, 12);

  // ADD GOAL ASSIGNMENT
  const assignGoalApiHandler = async (data) => {
    try {
      const obj = {
        goal: newGoal,
        userId: paramId,
      };
      console.log("Assigning goal with data:", obj);
      const response = await assignGolasInsidePeople(obj);
      console.log("Goal assigned successfully:", response);

      if (response.code == 200) {
        getUserByIdApiHandler();
        setShowModal({
          show: true,
          message: response.message,
          showButton: false,
        });

        // Reset newGoal state
        setNewGoal("");
      }
    } catch (error) {
      handleApiError(error, {
        showModal: true,
      });
    }
  };

  // DELETE GOALS INSIDE PEOPLE
  const deleteGoalApiHandler = async (id) => {
    try {
      const response = await deleteGoalInsidePeople({ goalId: id });
      if (response.code === 200) {
        getUserByIdApiHandler();
        setShowModal({
          show: true,
          message: response.message,
          showButton: false,
        });
      }
    } catch (error) {
      handleApiError(error, {
        showModal: true,
      });
    }
  };

  // GET ALL TEAMS API
  const [teamsData, setTeamsData] = useState([]);

  const getAllTeamsApiHandler = async () => {
    try {
      const obj = {
        // pageNo: 1,
        // pageSize: 100,
      };
      const response = await getAllTeamsApi(obj);
      setTeamsData(response.data);
    } catch (error) {
      handleApiError(error, {
        showModal: true,
      });
    }
  };

  const SKILLS_DISPLAY_LIMIT = 3; // Define the limit for skills in performance categories

  return (
    <div className="profile-page-unique">
      <BackButton />

      <main className="profile-main-unique">
        <div className="container">
          <h1
            style={{
              textAlign: "center",
              fontWeight: "600",
              marginBottom: "10px",
            }}
          >
            {t("candidateProfile.pageTitle")}
          </h1>

          {/* Profile Card */}
          <ProfileCard
            icon={<User size={48} />}
            safeCandidate={safeCandidate}
            candidateStats={candidateStats}
          />

          {/* Edit Button - Moved below profile header */}
          <div className="profile-edit-section-unique">
            <button
              className="btn btn-primary profile-edit-btn-unique"
              onClick={handleEditProfile}
            >
              <Edit size={16} />
              {t("candidateProfile.editProfile")}
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="tab-navigation-unique">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-button-unique ${
                  activeTab === tab.id ? "active" : ""
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="tab-content-unique">
            {activeTab === "overview" && (
              <div className="tab-panel-unique">
                <div className="profile-content-unique">
                  <div className="profile-left">
                    <div className="profile-section-unique">
                      <h3>
                        <BookOpen size={20} />
                        {t("candidateProfile.overview.educationExperience")}
                      </h3>
                      <div className="education-info-unique">
                        <div className="education-item-unique">
                          <strong>
                            {t("candidateProfile.overview.education")}
                          </strong>
                          {safeCandidate?.education?.length ? (
                            safeCandidate?.education.map((edu, index) => (
                              <div
                                key={index}
                                className="education-detail-unique"
                              >
                                <span className="institution-unique">
                                  {edu?.degree}
                                </span>
                              </div>
                            ))
                          ) : (
                            <span>{t("common.notAvailable")}</span>
                          )}

                          {/* <span>
                            {safeCandidate?.education[0]?.degree ||
                              t("common.notAvailable")}
                          </span> */}
                        </div>
                        <div className="education-item-unique">
                          <strong>
                            {t("candidateProfile.overview.experience")}
                          </strong>

                          <span>{safeCandidate?.experience}</span>
                        </div>
                      </div>
                    </div>
                    <div className="profile-section-unique">
                      <h3>
                        <Briefcase size={20} />
                        {t("candidateProfile.overview.keyProjects")}
                      </h3>
                      <div className="projects-unique">
                        {safeCandidate?.projects.length === 0 ? (
                          <span className="no-data-unique">
                            {t("common.notAvailable")}
                          </span>
                        ) : (
                          safeCandidate?.projects.map((project, index) => (
                            <div key={index} className="project-item-unique">
                              <span>{project.title}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="profile-right">
                    <div className="profile-section-unique">
                      <h3>
                        <Briefcase size={20} />
                        {t("candidateProfile.overview.professionalDetails")}
                      </h3>
                      <div className="professional-details-unique">
                        <div className="detail-item-unique">
                          <Users size={16} />
                          <span>
                            <strong>
                              {t("candidateProfile.overview.directReports")}
                            </strong>{" "}
                            {safeCandidate?.level
                              ? safeCandidate?.level
                              : t("common.notAvailable")}
                          </span>
                        </div>
                        <div className="detail-item-unique">
                          <User size={16} />
                          <span>
                            <strong>
                              {t("candidateProfile.overview.manager")}
                            </strong>{" "}
                            {safeCandidate?.managerName
                              ? safeCandidate?.managerName
                              : t("common.notAvailable")}
                          </span>
                        </div>
                        <div className="detail-item-unique">
                          <TrendingUp size={16} />
                          <span>
                            <strong>
                              {t("candidateProfile.overview.performanceRating")}
                            </strong>{" "}
                            {safeCandidate?.performanceRating
                              ? safeCandidate?.performanceRating
                              : t("common.notAvailable")}
                            {/* {safeCandidate?.teamDetails?.length
                              ? safeCandidate.teamDetails[0].teamFit + "%"
                              : t("common.notAvailable")} */}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="profile-section-unique">
                      <h3>
                        <Award size={20} />
                        {t("candidateProfile.overview.certifications")}
                      </h3>
                      <div className="certifications-unique">
                        {safeCandidate?.certifications.length ? (
                          safeCandidate?.certifications.map((cert, index) => (
                            <div
                              key={index}
                              className="certification-item-unique"
                            >
                              <Check size={16} />
                              <span>{cert.title}</span>
                            </div>
                          ))
                        ) : (
                          <span className="no-data-unique">
                            {t("common.notAvailable")}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="profile-section-unique">
                      <h3>
                        <Globe size={20} />
                        {t("candidateProfile.overview.languagesInterests")}
                      </h3>
                      <div className="languages-interests-unique">
                        <div className="section-item-unique">
                          {/* <strong>
                            {t("candidateProfile.overview.languages")}
                          </strong> */}
                          <div className="tags-unique">
                            {safeCandidate?.languages.length ? (
                              safeCandidate?.languages.map((lang, index) => (
                                <span key={index} className="tag-unique">
                                  {lang}
                                </span>
                              ))
                            ) : (
                              <span className="tag-unique">
                                {t("common.notAvailable")}
                              </span>
                            )}
                          </div>
                        </div>
                        {/* <div className="section-item-unique">
                          <strong>
                            {t("candidateProfile.overview.interests")}
                          </strong>
                          <div className="tags-unique">
                            {safeCandidate?.interests.length ? (
                              safeCandidate?.interests.map(
                                (interest, index) => (
                                  <span key={index} className="tag-unique">
                                    {interest}
                                  </span>
                                )
                              )
                            ) : (
                              <span className="tag-unique">
                                {t("common.notAvailable")}
                              </span>
                            )}
                          </div>
                        </div> */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "skills" && (
              <div className="tab-panel-unique">
                <div className="skills-content-unique">
                  <div className="c-skills-section-unique">
                    <div className="skills-header-unique">
                      <h3
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Award size={20} />
                        {t("candidateProfile.skills.jobSkillsAssessment")}
                        <CustomTooltip
                          tooltip={{
                            title: t(
                              "candidateProfile.skills.tooltips.jobSkills.title",
                            ),
                            message: t(
                              "candidateProfile.skills.tooltips.jobSkills.message",
                            ),
                          }}
                          id="job-skills-assessment-tooltip"
                        >
                          <Info
                            size={16}
                            style={{
                              marginTop: "10px",
                              color: "var(--text-secondary)",
                              cursor: "pointer",
                            }}
                          />
                        </CustomTooltip>
                      </h3>
                    </div>
                    <div className="skills-grid-unique">
                      {safeCandidate.jobSkills?.map((skill, index) => (
                        <div key={index} className="skill-item-unique">
                          <span className="skill-name-unique">{skill}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="skill-gaps-section-unique">
                    <h3
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        marginBottom: "20px",
                      }}
                    >
                      <Target size={20} />
                      {t("candidateProfile.skills.skillGapsUpskilling")}
                      <CustomTooltip
                        tooltip={{
                          title: t(
                            "candidateProfile.skills.tooltips.skillGaps.title",
                          ),
                          message: t(
                            "candidateProfile.skills.tooltips.skillGaps.message",
                          ),
                        }}
                        id="skill-gaps-upskilling-tooltip"
                      >
                        <Info
                          size={16}
                          style={{
                            marginTop: "13px",
                            color: "var(--text-secondary)",
                            cursor: "pointer",
                          }}
                        />
                      </CustomTooltip>
                    </h3>

                    {/* Job Skills Section */}
                    <div style={{ marginBottom: "32px" }}>
                      <h4
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: "var(--text-primary)",
                          marginBottom: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Briefcase size={16} />
                        {t("candidateProfile.skills.jobSkills")}
                      </h4>
                      {safeCandidate.softSkillsGaps.length === 0 && (
                        <span className="no-data-unique">
                          {t("candidateProfile.skills.noSkillsAvailable")}
                        </span>
                      )}
                      <div className="skill-gaps-grid-unique">
                        {safeCandidate?.jobSkillsGaps?.map((gap, index) => (
                          <div key={index} className="skill-gap-item-unique">
                            <div className="skill-gap-name-unique">{gap}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Soft Skills Section */}
                    <div>
                      <h4
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: "var(--text-primary)",
                          marginBottom: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Brain size={16} />
                        {t("candidateProfile.skills.softSkills")}
                      </h4>
                      <div className="skill-gaps-grid-unique">
                        {safeCandidate?.softSkillsGaps?.map((skill, index) => (
                          <div key={index} className="skill-gap-item-unique">
                            <div className="skill-gap-name-unique">{skill}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="performance-section-unique">
                    <h3>
                      <TrendingUp size={20} />
                      {t(
                        "candidateProfile.skills.tooltips.performanceCategories.title",
                      )}
                      <CustomTooltip
                        tooltip={{
                          title: t(
                            "candidateProfile.skills.tooltips.performanceCategories.title",
                          ),
                          message: t(
                            "candidateProfile.skills.tooltips.performanceCategories.message",
                          ),
                        }}
                        id="performance-categories-tooltip"
                      >
                        <Info
                          size={16}
                          style={{
                            marginTop: "13px",
                            color: "var(--text-secondary)",
                            cursor: "pointer",
                          }}
                        />
                      </CustomTooltip>
                    </h3>
                    <div className="performance-grid-unique">
                      {safeCandidate?.performanceCategories?.map(
                        (category, index) => {
                          const current = category?.currentSkills ?? [];
                          const gaps =
                            category?.skillGaps ?? category?.gaps ?? []; // ← normalize here

                          return (
                            <div
                              key={index}
                              className="performance-item-unique"
                            >
                              <div className="performance-label-unique">
                                {category?.name}
                              </div>

                              <div className="skill-gaps-unique">
                                <div className="skills-section-label-unique">
                                  {t("candidateProfile.skills.currentSkills")}
                                </div>

                                <div className="skills-pills-container-unique">
                                  {current.length === 0 && (
                                    <span className="no-data-unique">
                                      {t(
                                        "candidateProfile.skills.skillsNotAvailable",
                                      )}
                                    </span>
                                  )}

                                  {current
                                    .slice(0, SKILLS_DISPLAY_LIMIT)
                                    .map((skill, i) => (
                                      <span
                                        key={i}
                                        className="skill-pill-unique current-skill-pill-unique"
                                      >
                                        {skill}
                                      </span>
                                    ))}

                                  {current.length > SKILLS_DISPLAY_LIMIT && (
                                    <button
                                      className="skill-pill-unique current-skill-pill-unique skill-more-btn-unique"
                                      onClick={() =>
                                        handleOpenAllSkillsModal(
                                          current,
                                          `${t(
                                            "candidateProfile.skills.currentSkills",
                                          )} - ${category.name}`,
                                        )
                                      }
                                    >
                                      +{current.length - SKILLS_DISPLAY_LIMIT}{" "}
                                      {t("employees.results.more")}
                                    </button>
                                  )}
                                </div>

                                <div
                                  className="skills-section-label-unique"
                                  style={{ marginTop: "20px" }}
                                >
                                  {t("candidateProfile.skills.skillGaps")}
                                </div>

                                <div className="skills-pills-container-unique">
                                  {gaps.length === 0 && (
                                    <span className="no-data-unique">
                                      {t(
                                        "candidateProfile.skills.gapsNotIdentified",
                                      )}
                                    </span>
                                  )}

                                  {gaps
                                    .slice(0, SKILLS_DISPLAY_LIMIT)
                                    .map((gap, i) => (
                                      <span
                                        key={i}
                                        className="skill-pill-unique gap-skill-pill-unique"
                                      >
                                        {gap}
                                      </span>
                                    ))}

                                  {gaps.length > SKILLS_DISPLAY_LIMIT && (
                                    <button
                                      className="skill-pill-unique gap-skill-pill-unique skill-more-btn-unique"
                                      onClick={() =>
                                        handleOpenAllSkillsModal(
                                          gaps,
                                          `${t(
                                            "candidateProfile.skills.skillGaps",
                                          )} - ${category.name}`,
                                        )
                                      }
                                    >
                                      +{gaps.length - SKILLS_DISPLAY_LIMIT}{" "}
                                      {t("employees.results.more")}
                                    </button>
                                  )}
                                </div>
                              </div>

                              <div
                                className={`performance-score-unique ${getScoreColor(
                                  category?.score,
                                )}`}
                              >
                                {category?.score}/10
                              </div>
                            </div>
                          );
                        },
                      )}
                    </div>
                  </div>

                  <div className="soft-skills-section-unique">
                    <div className="skills-header-unique">
                      <h3>
                        <Brain size={20} />
                        {t("candidateProfile.skills.softSkillsAssessment")}
                        <CustomTooltip
                          tooltip={{
                            title: t(
                              "candidateProfile.skills.tooltips.softSkills.title",
                            ),
                            message: t(
                              "candidateProfile.skills.tooltips.softSkills.message",
                            ),
                          }}
                          id="soft-skills-tooltip"
                        >
                          <Info
                            size={16}
                            style={{
                              marginTop: "13px",
                              color: "var(--text-secondary)",
                              cursor: "pointer",
                            }}
                          />
                        </CustomTooltip>
                      </h3>
                      <div className="skills-search-unique">
                        <Search size={16} className="search-icon" />
                        <input
                          type="text"
                          placeholder={t(
                            "candidateProfile.skills.searchSkills",
                          )}
                          value={skillsSearchTerm}
                          onChange={(e) => setSkillsSearchTerm(e.target.value)}
                          className="skills-search-input-unique"
                        />
                      </div>
                    </div>
                    <div className="soft-skills-grid-unique">
                      {displayedSkills?.map((skill, index) => (
                        <div key={index} className="soft-skill-item-unique">
                          <div className="soft-skill-header-unique">
                            {/* <span className="soft-skill-name-unique">
                              {skill?.name}159

                            </span> */}
                            <span className="skill-name-unique">
                              {skill.name ?? skill}
                            </span>
                          </div>
                          <div
                            className="soft-skill-progress-unique"
                            style={{ marginTop: "12px" }}
                          >
                            <div className="progress-bar-container-unique">
                              <div
                                className="progress-bar-unique"
                                style={{
                                  width: `${(skill?.score / 10) * 100}%`,
                                }}
                              ></div>
                            </div>
                            <span className="progress-score-unique">
                              {skill?.score}/10
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    {!showAllSkills && filteredSkills.length > 12 && (
                      <div className="load-more-container-unique">
                        <button
                          className="btn btn-outline load-more-btn-unique"
                          onClick={() => setShowAllSkills(true)}
                        >
                          {/* {t("candidateProfile.skills.loadMoreSkills")} (
                          {filteredSkills?.length - 12} remaining) */}
                          {t("candidateProfile.skills.loadMoreSkills")} (
                          {t("candidateProfile.skills.remaining", {
                            count: filteredSkills?.length - 12,
                          })}
                          )
                        </button>
                      </div>
                    )}
                    {showAllSkills && (
                      <div className="load-more-container-unique">
                        <button
                          className="btn btn-outline load-more-btn-unique"
                          onClick={() => setShowAllSkills(false)}
                        >
                          {t("candidateProfile.skills.showLess")}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "development" && (
              <div className="tab-panel-unique">
                <div className="dng-wrapper">
                  <div className="dng-header">
                    <h3 className="dng-title">
                      {t("candidateProfile.development.title")}
                      <CustomTooltip
                        tooltip={{
                          title: t(
                            "candidateProfile.development.tooltips.developmentAndGoals.title",
                          ),
                          message: t(
                            "candidateProfile.development.tooltips.developmentAndGoals.message",
                          ),
                        }}
                        id="development-goals-tooltip"
                        className="profile-mobile-sheet-tooltip"
                      >
                        <button
                          type="button"
                          className="profile-mobile-tooltip-trigger"
                          aria-label={t(
                            "candidateProfile.development.tooltips.developmentAndGoals.title",
                          )}
                          onClick={(event) => event.currentTarget.focus()}
                        >
                          <Info size={16} />
                        </button>
                      </CustomTooltip>
                    </h3>
                    <div
                      className="dng-toggle"
                      role="tablist"
                      aria-label={t(
                        "candidateProfile.development.skillTypeAriaLabel",
                      )}
                    >
                      <button
                        className={`dng-toggle-btn ${
                          dngActiveSkillType === "job" ? "active" : ""
                        }`}
                        onClick={() => setDngActiveSkillType("job")}
                        role="tab"
                        aria-selected={dngActiveSkillType === "job"}
                      >
                        {t("candidateProfile.development.jobSkills")}
                      </button>
                      <button
                        className={`dng-toggle-btn ${
                          dngActiveSkillType === "soft" ? "active" : ""
                        }`}
                        onClick={() => setDngActiveSkillType("soft")}
                        role="tab"
                        aria-selected={dngActiveSkillType === "soft"}
                      >
                        {t("candidateProfile.development.softSkills")}
                      </button>
                    </div>
                  </div>

                  <div className="dng-skill-grid">
                    {dngSkillsForTab.length === 0 ? (
                      <div className="dng-empty">{t("common.noDataFound")}</div>
                    ) : (
                      dngSkillsForTab.map((skill, idx) => {
                        const skillName =
                          typeof skill === "string" ? skill : skill?.name || "";
                        // you don't actually use `status` below, so it can be removed if you want
                        // const status = dngSkillStatus(skill);
                        const recommendations = dngRecommendedCourses(skill);
                        const statusKey = hasLD
                          ? skillStatusFromCourses(skill)
                          : dngSkillStatus(skill);

                        const rawSkillStatus = skill?.skillStatus; // e.g. "Not Started", "In Progress", "Completed"

                        // what text to show inside the chip
                        const statusText =
                          rawSkillStatus ?? statusLabelFromKey(statusKey);

                        return (
                          <div key={idx} className="dng-skill-card">
                            <div className="dng-skill-card-header">
                              <div className="dng-skill-title">{skillName}</div>
                              {/* <span
                                className={`dng-status-chip dng-${statusKey}-chip`}
                                title={`Status: ${statusLabelFromKey(
                                  statusKey
                                )}`}
                              >
                                {statusLabelFromKey(statusKey)}
                              </span> */}

                              <span
                                className={`dng-status-chip dng-${statusKey}-chip`}
                                title={`${t("candidateProfile.development.statusPrefix")} ${statusText}`}
                              >
                                {statusText}
                              </span>
                            </div>

                            <div className="dng-courses-grid">
                              {(() => {
                                const visibleCourses = (
                                  recommendations || []
                                ).filter((c) => !dngIsDeleted(skillName, c.id));

                                if (visibleCourses.length === 0) {
                                  return (
                                    <div
                                      className="dng-empty dng-no-courses"
                                      style={{ gridColumn: "1 / -1" }}
                                    >
                                      {t(
                                        "candidateProfile.development.noCoursesAvailable",
                                      )}
                                    </div>
                                  );
                                }

                                return visibleCourses.map((course) => {
                                  const assigned = hasLD
                                    ? !!course.is_assigned
                                    : dngGoals.some(
                                        (g) =>
                                          g.skill === skillName &&
                                          g.course.id === course.id,
                                      );

                                  const goal =
                                    dngGoals.find(
                                      (g) =>
                                        g.skill === skillName &&
                                        g.course.id === course.id,
                                    ) || null;

                                  const dueDate = hasLD
                                    ? course.due_date
                                      ? new Date(course.due_date)
                                      : null
                                    : goal?.dueDate
                                      ? new Date(goal.dueDate)
                                      : null;

                                  const isPast = !!(
                                    dueDate && dueDate < new Date()
                                  );
                                  const courseCompleted =
                                    isCourseCompleted(course);
                                  const courseStatusText = String(
                                    course.status || "",
                                  )
                                    .toLowerCase()
                                    .includes("complete")
                                    ? "Completed"
                                    : "Assigned";

                                  return (
                                    <div
                                      key={course.id}
                                      className={`dng-course-card ${
                                        courseCompleted
                                          ? "dng-course-card-muted"
                                          : ""
                                      }`}
                                      aria-disabled={courseCompleted}
                                    >
                                      <div className="dng-course-card-header">
                                        <div className="dng-course-icon">
                                          <PlayCircle size={18} />
                                        </div>
                                        <div
                                          style={{ display: "flex", gap: 8 }}
                                        >
                                          <div className="dng-course-level-badge">
                                            <span className="dng-level-text">
                                              {course.level ||
                                                t(
                                                  "candidateProfile.development.noLevel",
                                                )}
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="dng-course-content">
                                        <h5 className="dng-course-title">
                                          {course.course_name}
                                        </h5>
                                        <div className="dng-course-meta">
                                          <span className="dng-course-platform">
                                            {course.platform}
                                          </span>
                                          <span className="dng-course-rating">
                                            {Number(course.rating).toFixed(1)}★
                                          </span>
                                        </div>
                                      </div>

                                      <div className="dng-course-footer">
                                        {(() => {
                                          const goalLocal =
                                            dngGoals.find(
                                              (g) =>
                                                g.skill === skillName &&
                                                g.course.id === course.id,
                                            ) || null;

                                          if (assigned || courseCompleted) {
                                            return (
                                              <div className="dng-assigned-row">
                                                {dueDate && (
                                                  <div
                                                    style={{ marginTop: "0px" }}
                                                    className={`dng-due-hint ${
                                                      isPast
                                                        ? "past"
                                                        : "current"
                                                    }`}
                                                  >
                                                    {t(
                                                      "candidateProfile.development.due",
                                                    )}{" "}
                                                    {dueDate.toLocaleDateString()}
                                                  </div>
                                                )}
                                                <span className="dng-status-chip dng-current-chip">
                                                  {t(
                                                    "candidateProfile.development.assigned",
                                                  )}
                                                </span>
                                              </div>
                                            );
                                          }

                                          if (goalLocal) {
                                            return (
                                              <div className="dng-assigned-row">
                                                <label
                                                  className="dng-date-wrap"
                                                  title={t(
                                                    "candidateProfile.development.selectDueDateTitle",
                                                  )}
                                                >
                                                  <span
                                                    style={{ fontSize: "12px" }}
                                                  >
                                                    {t(
                                                      "candidateProfile.development.dueDate",
                                                    )}{" "}
                                                  </span>
                                                  <Calendar size={14} />
                                                  <input
                                                    type="date"
                                                    className="dng-date-input"
                                                    min={todayMin} // ⟵ blocks & greys out past dates
                                                    value={
                                                      goalLocal.dueDate || ""
                                                    }
                                                    onChange={(e) =>
                                                      dngUpdateDueDate(
                                                        skillName,
                                                        course.id,
                                                        e.target.value,
                                                      )
                                                    }
                                                  />
                                                </label>

                                                <button
                                                  className="dng-btn dng-btn-ghost"
                                                  onClick={() =>
                                                    dngConfirmAssignment(
                                                      skillName,
                                                      course,
                                                    )
                                                  }
                                                  title={t(
                                                    "candidateProfile.development.confirmAssignment",
                                                  )}
                                                >
                                                  {t("common.confirm")}
                                                </button>

                                                <button
                                                  className="dng-btn dng-btn-danger"
                                                  onClick={() =>
                                                    dngClearAssignment(
                                                      skillName,
                                                      course.id,
                                                    )
                                                  }
                                                  title={t("common.cancel")}
                                                >
                                                  {t("common.cancel")}
                                                </button>
                                              </div>
                                            );
                                          }

                                          return (
                                            <button
                                              className="dng-btn dng-btn-primary"
                                              onClick={() =>
                                                dngAssignCourse(
                                                  skillName,
                                                  course,
                                                )
                                              }
                                            >
                                              {t(
                                                "candidateProfile.development.assign",
                                              )}
                                            </button>
                                          );
                                        })()}

                                        <button
                                          className="dng-inline-delete"
                                          onClick={() =>
                                            dngDeleteCourse(
                                              skillName,
                                              course.id,
                                            )
                                          }
                                          title={t(
                                            "candidateProfile.development.hideRecommendation",
                                          )}
                                        >
                                          <Trash2 size={14} />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/*  filters
                  <div className="dng-legend">
                    <span className="dng-status-chip dng-pending-chip">
                      Not Started
                    </span>
                    <span className="dng-status-chip dng-current-chip">
                      Pursuing
                    </span>
                    <span className="dng-status-chip dng-past-chip">
                      Completed
                    </span>
                  </div> */}

                  <div className="dng-goals-block">
                    <div className="dng-goals-header">
                      <h4>
                        {t("candidateProfile.development.goalsObjectives")}
                      </h4>
                      <p className="dng-goals-sub">
                        {t("candidateProfile.development.goalsDescription")}
                      </p>
                    </div>
                    {apiGoals.assigned.length === 0 ? (
                      <div className="dng-empty">
                        {t("candidateProfile.development.noGoalsAssigned")}
                      </div>
                    ) : (
                      <div className="dng-goals-table" role="table">
                        <div
                          className="dng-goals-row dng-goals-head"
                          role="row"
                        >
                          <div className="cell">
                            {t("candidateProfile.development.table.skill")}
                          </div>
                          <div className="cell">
                            {t("candidateProfile.development.table.course")}
                          </div>
                          <div className="cell">
                            {t("candidateProfile.development.table.dueDate")}
                          </div>
                          <div className="cell">
                            {t("candidateProfile.development.table.status")}
                          </div>
                        </div>

                        {apiGoals.assigned.map((row, i) => (
                          <div
                            className="dng-goals-row"
                            role="row"
                            key={`${row.id}-${i}`}
                          >
                            <div className="cell">{row.skill}</div>
                            <div className="cell">{row.course}</div>
                            <div className="cell">
                              {row.dueDate
                                ? new Date(row.dueDate).toLocaleDateString()
                                : "—"}
                            </div>
                            <div className="cell">
                              <span
                                className={`dng-status-chip ${statusChipClass(
                                  row.status,
                                )}`}
                              >
                                {row.status || "—"}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "team" && (
              <div className="tab-panel-unique">
                {/* ************************************************************ */}

                <div className="development-content-unique">
                  <div className="team-assigned-section-unique">
                    <h3>
                      <Users size={20} />
                      {t("candidateProfile.team.teamsAssignedTo", {
                        name: safeCandidate.name,
                      })}
                    </h3>

                    <div className="team-assigned-grid-unique">
                      {safeCandidate?.teamDetails?.length === 0 && (
                        <span className="no-data-unique">
                          {t("candidateProfile.team.noTeamsAssigned")}
                        </span>
                      )}
                      {safeCandidate?.teamDetails?.map((team, index) => (
                        <div key={index} className="team-assigned-card-unique">
                          <div className="team-assigned-card-header-unique">
                            <div className="team-assigned-info-unique">
                              <span className="team-assigned-name-unique">
                                {team.teamName}
                              </span>
                              <span className="team-assigned-lead-unique">
                                {t("candidateProfile.team.lead")}{" "}
                                {team.teamLead.name ||
                                  t("candidateProfile.team.notAvailableShort")}
                              </span>
                            </div>
                            <div
                              className={`team-assigned-match-badge-unique ${getMatchColor(
                                team.teamFit,
                              )}`}
                            >
                              {team.teamFit ? team.teamFit : 0}%
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="team-assignment-section-unique">
                    <h3>
                      <Plus size={20} />
                      {t("candidateProfile.team.teamAssignment")}
                      <CustomTooltip
                        tooltip={{
                          title: t(
                            "candidateProfile.skills.tooltips.teamAssignment.title",
                          ),
                          message: t(
                            "candidateProfile.skills.tooltips.teamAssignment.message",
                          ),
                        }}
                        id="team-assignment-tooltip"
                        className="profile-mobile-sheet-tooltip"
                      >
                        <button
                          type="button"
                          className="profile-mobile-tooltip-trigger"
                          aria-label={t(
                            "candidateProfile.skills.tooltips.teamAssignment.title",
                          )}
                          onClick={(event) => event.currentTarget.focus()}
                        >
                          <Info size={16} />
                        </button>
                      </CustomTooltip>
                    </h3>
                    <div className="team-assignment-unique">
                      <select
                        value={selectedTeam}
                        onChange={(e) => setSelectedTeam(e.target.value)}
                        className="team-select-unique"
                      >
                        <option value="">
                          {t("candidateProfile.team.selectTeam")}
                        </option>
                        {safeCandidate.teamMatches.map((team) => (
                          <option key={team._id} value={team._id}>
                            {team.teamName} ({team.teamFit || "0"}
                            {t("candidateProfile.team.match")})
                          </option>
                        ))}
                      </select>
                      <button
                        className="btn btn-primary"
                        onClick={handleAddToTeam}
                      >
                        {t("candidateProfile.team.addToTeam")}
                      </button>
                    </div>

                    <div className="team-matches-unique">
                      <h4>{t("candidateProfile.team.bestTeamMatches")}</h4>
                      {/* Updated to show teams in 2 columns */}
                      <div className="team-matches-grid-unique">
                        {teamsData.length === 0 && (
                          <span className="no-data-unique">
                            {t("candidateProfile.team.noTeamsFound")}
                          </span>
                        )}
                        {/* {safeCandidate?.teamMatches(0, 6)?.map((team) => ( */}
                        {(safeCandidate?.teamMatches ?? [])
                          .slice(0, 6)
                          .map((team) => (
                            <div
                              key={team.id}
                              className="team-match-item-unique"
                            >
                              <div className="team-match-info-unique">
                                <span
                                  className="team-name-unique"
                                  style={{ fontWeight: "600" }}
                                >
                                  {team?.teamName}
                                </span>
                                <span className="team-lead-unique">
                                  {t("candidateProfile.team.lead")}{" "}
                                  {team?.teamLead?.name}
                                </span>
                              </div>
                              <div
                                className={`match-score-unique ${getMatchColor(
                                  team?.teamFit,
                                )}`}
                              >
                                {team?.teamFit ? team?.teamFit : 0}%
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Complaint Section */}
          {/* <div className="complaint-section-unique">
            <button className="complaint-btn-unique" onClick={handleComplaint}>
              <MessageCircle size={16} />
              {t("candidateProfile.actions.reportIssue")}
            </button>
          </div> */}
        </div>
      </main>

      <Modal
        show={showModal.show}
        onClose={() =>
          setShowModal({ show: false, type: "", title: "", message: "" })
        }
        type={showModal.type}
        title={showModal.title}
        message={showModal.message}
        showButton={showModal.showButton}
        onConfirm={showModal.onConfirm}
      />

      {/* New All Skills Modal */}
      <AllSkillsModal
        show={showAllSkillsModal}
        title={modalSkillsTitle}
        skills={modalSkillsList}
        onClose={handleCloseAllSkillsModal}
      />

      <SubscriptionExpiredModal
        show={showSubscriptionExpired}
        message={subscriptionMessage}
        onRedirect={handleSubscriptionRedirect}
      />
    </div>
  );
};

export default CandidateProfilePage;
